import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";

export interface BuyerSellerDetails {
  // Common Details
  tcId: string;
  // Seller Details
  sellerLicenseNumber: string;
  sellingOnBehalfOfFlag: string;
  sellerName: string;
  sellerTEId: string;
  sellerAddress1: string;
  sellerAddress2: string;
  sellerAddress3: string;
  sellerPostcode: string;
  sellerTown: string;
  sellerStateOrProvince: string;
  sellerCountryOrArea: string;
  sellerCertifiedOrganizationName: string;
  sellerSCNumber: string;
  tcSellerProductDeclarations: string;
  // Buyer Details
  buyerLicenseNumber: string;
  buyingOnBehalfOfFlag: string;
  buyerName: string;
  buyerTEId: string;
  buyerAddress1: string;
  buyerAddress2: string;
  buyerAddress3: string;
  buyerPostcode: string;
  buyerTown: string;
  buyerStateOrProvince: string;
  buyerCountryOrArea: string;
  buyerCertifiedOrganizationName: string;
  buyerScCbLicensingCode: string;
  buyerSCNumber: string;
  isBuyerTeCertified: string;
  // Non-Certified Trader
  nonCertifiedTrader: INonCertifiedTrader[];
}

export interface INonCertifiedTrader {
  sellerNonCertifiedTraderName: string;
  sellerNonCertifiedTraderTeId: string;
}

export const buyerSellerDetails: BuyerSellerDetails = {
  // Common Details
  tcId: "",
  // Seller Details
  isBuyerTeCertified: "Yes",
  sellerLicenseNumber: "",
  sellingOnBehalfOfFlag: "Yes",
  sellerName: "",
  sellerTEId: "",
  sellerAddress1: "",
  sellerAddress2: "",
  sellerAddress3: "",
  sellerPostcode: "",
  sellerTown: "",
  sellerStateOrProvince: "",
  sellerCountryOrArea: "",
  sellerCertifiedOrganizationName: "",
  sellerSCNumber: "",
  tcSellerProductDeclarations: "",
  // Buyer Details
  buyerLicenseNumber: "",
  buyingOnBehalfOfFlag: "Yes",
  buyerName: "",
  buyerTEId: "",
  buyerAddress1: "",
  buyerAddress2: "",
  buyerAddress3: "",
  buyerPostcode: "",
  buyerTown: "",
  buyerStateOrProvince: "",
  buyerCountryOrArea: "",
  buyerCertifiedOrganizationName: "",
  buyerScCbLicensingCode: "",
  buyerSCNumber: "",
  // Non-Certified Trader
  nonCertifiedTrader: [],
};

const normalizeNaValue = (value?: string | null) => {
  if (typeof value === "string" && value.trim().toLowerCase() === "na") {
    return "na";
  }
  return value || "";
};

export const getTcSellerInformation = (data: any) =>
  data?.tcSellerInformation || data?.sellerInformation || data?.sellerDetails || {};

export const getTcBuyerInformation = (data: any) =>
  data?.tcBuyerInformation || data?.buyerInformation || data?.buyerDetails || {};

export const getTcSellerNonCertifiedTraderInformation = (data: any) =>
  data?.tcSellerNonCertifiedTraderInformation ||
  data?.sellerNonCertifiedTraderInformation ||
  [];

export const mapBuyerSellerDetailsApiResponseToInitialValues = (data: any) => {
  const sellerInformation = getTcSellerInformation(data);
  const buyerInformation = getTcBuyerInformation(data);

  return {
    tcId: data.tcId || "",
    // Seller Details - Updated to match API response structure
    sellerLicenseNumber: sellerInformation?.sellerCbClientNo || sellerInformation?.sellerLicenseNo || "",
    sellingOnBehalfOfFlag: sellerInformation?.sellingOnBehalfOfFlag || "Yes",
    sellerName: sellerInformation?.sellerName || "",
    sellerTEId: sellerInformation?.sellerTeId || "",
    sellerAddress1: sellerInformation?.sellerStreetAddress || sellerInformation?.sellerAddress1 || "",
    sellerAddress2: sellerInformation?.sellerAddress2 || "",
    sellerAddress3: sellerInformation?.sellerAddress3 || "",
    sellerPostcode: sellerInformation?.sellerPostcode || "",
    sellerTown: sellerInformation?.sellerTown || "",
    sellerStateOrProvince: sellerInformation?.sellerStateOrProvince || "",
    sellerCountryOrArea: sellerInformation?.sellerCountryOrArea || "",
    sellerCertifiedOrganizationName: sellerInformation?.sellerCertifiedOrganizationName || "",
    sellerSCNumber: sellerInformation?.sellerScNo || "",
    tcSellerProductDeclarations: sellerInformation?.tcSellerProductDeclarations || "",
    // Buyer Details - Updated to match API response structure
    buyerLicenseNumber: buyerInformation?.buyerCbClientNo || buyerInformation?.buyerLicenseNo || "",
    buyingOnBehalfOfFlag: buyerInformation?.buyingOnBehalfOfFlag || "Yes",
    buyerName: buyerInformation?.buyerName || "",
    buyerTEId: buyerInformation?.buyerTeId || "",
    buyerAddress1: buyerInformation?.buyerStreetAddress || buyerInformation?.buyerAddress1 || "",
    buyerAddress2: buyerInformation?.buyerAddress2 || "",
    buyerAddress3: buyerInformation?.buyerAddress3 || "",
    buyerPostcode: buyerInformation?.buyerPostcode || "",
    buyerTown: buyerInformation?.buyerTown || "",
    buyerStateOrProvince: buyerInformation?.buyerStateOrProvince || "",
    buyerCountryOrArea: buyerInformation?.buyerCountryOrArea || "",
    buyerCertifiedOrganizationName: buyerInformation?.buyerCertifiedOrganizationName || "",
    buyerScCbLicensingCode: buyerInformation?.buyerScCbLicensingCode || "",
    buyerSCNumber: buyerInformation?.buyerScNo || "",
    isBuyerTeCertified: buyerInformation?.isBuyerTeCertified || "Yes",
    // Non-Certified Trader
    nonCertifiedTrader: getTcSellerNonCertifiedTraderInformation(data)
  };
};

// For a Return TC the parties are swapped relative to the original (source) TC:
//   Return Seller  <-  Source Buyer
//   Return Buyer   <-  Source Seller
// This mirrors the backend cross-match in tc_v4_incoming_reference_validation
// (return seller == incoming buyer, return buyer == incoming seller). The source
// buyer may be a non-certified party, so its SC No / certified org / CB client No
// can legitimately be "na" / blank once copied onto the return seller.
export const mapReturnTcAutofillFromSource = (
  sourceData: any,
  returnTcId: string
): BuyerSellerDetails => {
  const sourceSeller = getTcSellerInformation(sourceData);
  const sourceBuyer = getTcBuyerInformation(sourceData);

  const normalizeFlag = (flag?: string) => {
    if (!flag) return "Yes";
    const cleaned = flag.trim().toUpperCase();
    if (cleaned === "Y" || cleaned === "YES") return "Yes";
    if (cleaned === "N" || cleaned === "NO") return "No";
    return "Yes";
  };

  const getFirstScNo = (scVal: any): string => {
    if (Array.isArray(scVal)) return scVal[0] || "";
    return scVal || "";
  };

  return {
    ...buyerSellerDetails,
    tcId: returnTcId || "",
    // Return Seller  <-  Source Buyer
    sellerLicenseNumber: sourceBuyer?.buyerCbClientNo || sourceBuyer?.buyerLicenseNo || "na",
    sellingOnBehalfOfFlag: normalizeFlag(sourceBuyer?.buyingOnBehalfOfFlag || sourceBuyer?.buyingOnBehalfOfFlag),
    sellerName: sourceBuyer?.buyerName || "",
    sellerTEId: sourceBuyer?.buyerTeId || "",
    sellerAddress1: sourceBuyer?.buyerStreetAddress || sourceBuyer?.buyerAddress1 || "",
    sellerAddress2: sourceBuyer?.buyerAddress2 || "",
    sellerAddress3: sourceBuyer?.buyerAddress3 || "",
    sellerPostcode: sourceBuyer?.buyerPostcode || "",
    sellerTown: sourceBuyer?.buyerTown || "",
    sellerStateOrProvince: sourceBuyer?.buyerStateOrProvince || "",
    sellerCountryOrArea: sourceBuyer?.buyerCountryOrArea || "",
    sellerCertifiedOrganizationName: sourceBuyer?.buyerCertifiedOrganizationName || "",
    sellerSCNumber: getFirstScNo(sourceBuyer?.buyerScNo),
    tcSellerProductDeclarations: "",
    // Return Buyer  <-  Source Seller
    buyerLicenseNumber: sourceSeller?.sellerCbClientNo || sourceSeller?.sellerLicenseNo || "",
    buyingOnBehalfOfFlag: normalizeFlag(sourceSeller?.sellingOnBehalfOfFlag),
    buyerName: sourceSeller?.sellerName || "",
    buyerTEId: sourceSeller?.sellerTeId || "",
    buyerAddress1: sourceSeller?.sellerStreetAddress || sourceSeller?.sellerAddress1 || "",
    buyerAddress2: sourceSeller?.sellerAddress2 || "",
    buyerAddress3: sourceSeller?.sellerAddress3 || "",
    buyerPostcode: sourceSeller?.sellerPostcode || "",
    buyerTown: sourceSeller?.sellerTown || "",
    buyerStateOrProvince: sourceSeller?.sellerStateOrProvince || "",
    buyerCountryOrArea: sourceSeller?.sellerCountryOrArea || "",
    buyerCertifiedOrganizationName: sourceSeller?.sellerCertifiedOrganizationName || "",
    buyerScCbLicensingCode:
      sourceSeller?.sellerCbLicensingCode || sourceData?.tcCbLicensingCode || "",
    buyerSCNumber: getFirstScNo(sourceSeller?.sellerScNo),
    isBuyerTeCertified: "Yes",
    // Non-certified trader is seller-role specific on the return TC; default to the
    // "na" placeholder so the (min 1) requirement is satisfied for review.
    nonCertifiedTrader: [
      { sellerNonCertifiedTraderName: "na", sellerNonCertifiedTraderTeId: "" },
    ],
  };
};

export const mapPayload = (jsonData: any, tcId: any, buyerStates?: any, sellerStates?: any,sellerScStandards?: string[], isReturnTc?: boolean) => {
  const buyerIsCertified = jsonData.isBuyerTeCertified !== "No";
  return {
    tcId: tcId,
    // Sent so the backend (container app) scopes the relaxed certified-seller rule to
    // Return TCs only — the return seller mirrors the original buyer and may be non-certified.
    tcIsReturn: isReturnTc ? "Yes" : "No",
    tcSellerInformation: {
      sellerCbClientNo: jsonData.sellerLicenseNumber || "",
      sellingOnBehalfOfFlag: jsonData.sellingOnBehalfOfFlag || "Yes", // Default value as per the payload
      sellerName: jsonData.sellerName || "",
      sellerTeId: jsonData.sellerTEId || "",
      sellerStreetAddress: jsonData.sellerAddress1 || "",
      sellerAddress2: jsonData.sellerAddress2 || "",
      sellerAddress3: jsonData.sellerAddress3 || "",
      sellerPostcode: normalizeNaValue(jsonData.sellerPostcode),
      sellerTown: normalizeNaValue(jsonData.sellerTown),
      sellerStateOrProvince: jsonData.sellerStateOrProvince || "",
      sellerCountryOrArea: jsonData.sellerCountryOrArea || "",
      sellerCertifiedOrganizationName: jsonData.sellerCertifiedOrganizationName || "",
      sellerScNo: jsonData.sellerSCNumber || "",
      sellerScStandard: sellerScStandards || [],
      tcSellerProductDeclarations: jsonData.tcSellerProductDeclarations || "",
      sellerCountryOrAreaDescription: useTransactionCertificateStore.getState().getCountryCodes.find((option: any) => option.value === (jsonData.sellerCountryOrArea ?? ""))?.label ?? "",  
      sellerStateOrProvinceDescription: sellerStates.find((option: any) => option.value === (jsonData.sellerStateOrProvince ?? ""))?.label ?? ""
    },
    tcBuyerInformation: {
      isBuyerTeCertified: jsonData.isBuyerTeCertified || "Yes", // Default value as per the payload
      buyerCbClientNo: jsonData.buyerLicenseNumber || "", // Default value as per the payload
      buyingOnBehalfOfFlag: jsonData.buyingOnBehalfOfFlag || "Yes", // Default value as per the payload
      buyerName: jsonData.buyerName || "",
      buyerTeId: jsonData.buyerTEId || "",
      buyerStreetAddress: jsonData.buyerAddress1 || "",
      buyerAddress2: jsonData.buyerAddress2, // Not available in the source JSON
      buyerAddress3: jsonData.buyerAddress3, // Not available in the source JSON
      buyerPostcode: normalizeNaValue(jsonData.buyerPostcode),
      buyerTown: normalizeNaValue(jsonData.buyerTown),
      buyerStateOrProvince: jsonData.buyerStateOrProvince || "",
      buyerCountryOrArea: jsonData.buyerCountryOrArea || "",
      buyerCertifiedOrganizationName: buyerIsCertified ? normalizeNaValue(jsonData.buyerCertifiedOrganizationName) : "na",
      buyerScCbLicensingCode: buyerIsCertified ? jsonData.buyerScCbLicensingCode || "" : "",
      buyerScNo: buyerIsCertified ? normalizeNaValue(jsonData.buyerSCNumber) : "na",
      buyerCountryOrAreaDescription: useTransactionCertificateStore.getState().getCountryCodes.find((option: any) => option.value === (jsonData.buyerCountryOrArea ?? ""))?.label ?? "",
      buyerStateOrProvinceDescription: buyerStates.find((option: any) => option.value === (jsonData.buyerStateOrProvince ?? ""))?.label ?? ""
    },
    tcSellerNonCertifiedTraderInformation: (jsonData.nonCertifiedTrader || []).map((trader: INonCertifiedTrader) => ({
      sellerNonCertifiedTraderName: normalizeNaValue(trader.sellerNonCertifiedTraderName),
      sellerNonCertifiedTraderTeId: trader.sellerNonCertifiedTraderTeId || "",
    })),
  };
};
